// [Phase 6a foundation] TanStack Query client + sensible defaults.
//
// Defaults chosen for the admin/MCP read path:
//   - staleTime: 30 s — the audit log + sources card don't change
//     second-by-second; refetching on every focus is wasteful.
//   - retry: 1 — Cloud Run + IAP gives transient 401s on token-refresh
//     races; a single retry catches those.
//   - refetchOnWindowFocus: true — but throttled by staleTime.
//   - refetchOnReconnect: true — survives flaky office Wi-Fi.
//
// One client per app instance; the QueryClientProvider wraps the
// React tree in main.tsx. Tests construct a fresh client with
// `staleTime: Infinity` + `retry: false` so transient assertions
// don't depend on timing.
import { QueryClient } from "@tanstack/react-query";
import { ApiError, isBackendUnreachableError } from "./api";

// [dev-loop bugfix] Mutations default to retry:false below, so a request that
// never reached the API fails PERMANENTLY while the page's queries quietly
// recover through the same outage. That asymmetry is what turned a ~2s backend
// restart into "Couldn't attach <file>: API 500" — the upload is a mutation.
//
// This is the opt-in policy for mutations where a retry is SAFE. It fires only
// on an unreachable error (no answer from the API — see isBackendUnreachableError),
// never on a real 4xx/5xx the backend actually produced, so a rejected file or a
// forbidden tenant still fails immediately with its own message.
//
// Why opt-in and not the global default: status 0 means the CONNECTION failed,
// not that the server provably never saw the request. For an attachment upload
// the worst case is a duplicate attachment the user can see and remove; for a
// mutation that spends money or creates a document (draft generation, sending a
// chat turn) a silent double-execution is worse than the visible failure. Add
// this to a mutation only after deciding a repeat is acceptable.
export const RETRY_IF_UNREACHABLE = {
  retry: (failureCount: number, err: unknown): boolean =>
    isBackendUnreachableError(err) && failureCount < 2,
  // A dev-loop tsx restart or a Cloud Run cold start is seconds, not
  // milliseconds — 400ms then 1.6s covers both without stalling a real outage.
  retryDelay: (attempt: number): number => 400 * 4 ** attempt,
} as const;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // An unreachable error gets the SAME budget a safe mutation gets
        // (RETRY_IF_UNREACHABLE): 2 retries at 400ms then 1.6s. A GET is
        // idempotent, so the double-execution worry that keeps mutations
        // opt-in does not apply here.
        //
        // Why it matters: apps/admin runs on App Hosting with minInstances 0,
        // so after an idle period the first request cold-boots the container
        // and the edge answers with a non-JSON 5xx meanwhile. The old policy —
        // one retry on React Query's ~1s default backoff — put both attempts
        // inside the first second or two and surfaced "Backend unreachable"
        // for a backend that was merely still starting.
        retry: (failureCount, err) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            return false;
          }
          if (isBackendUnreachableError(err)) return failureCount < 2;
          return failureCount < 1;
        },
        retryDelay: (attempt, err) =>
          isBackendUnreachableError(err)
            ? 400 * 4 ** attempt
            : Math.min(1000 * 2 ** attempt, 30_000),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
